import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router";
import type { AppState, MemberRole, PlatformRole, ServerHealth, ServerState } from "../lib/types";
import { Icon, type IconName } from "./icons";

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// ----------------------------------------------------------------- button

/*
 * The emphasis ladder, and there is no rung between these. It is the customer
 * dashboard's ladder, rung for rung, because an operator and a customer looking
 * at the same kind of decision should be reading the same kind of button.
 *
 *   primary    the one action you opened the screen to take
 *   outline    promoted, but sharing the screen with a primary
 *   secondary  anything with a peer beside it
 *   ghost      row-level and tertiary; no chrome until hover
 *   danger     destructive, and only ever destructive
 *
 * `primary` is the solid inverse block. It used to be a filled `ops-600` amber
 * block, which made amber both this console's identity signal and its call to
 * action — see `--color-inverse` in index.css for why those cannot be the same
 * colour. The mirror of the customer dashboard's rule holds here: **amber is not
 * a button.** Amber means a link, the active nav item, or focus, so anything
 * amber on a screen is somewhere you can go rather than something that acts.
 *
 * `danger` is outlined rather than a filled red block for the same reason it is
 * in the customer dashboard: a destructive action should be legible, not loud.
 * The confirmation dialog is what makes it deliberate.
 */
type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "inverse";

const PRIMARY = "bg-inverse text-on-inverse hover:opacity-90 disabled:opacity-40";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: PRIMARY,
  secondary:
    "border border-line bg-transparent text-fg hover:border-line-strong hover:bg-raised disabled:text-fg-subtle disabled:hover:bg-transparent",
  outline:
    "border border-accent/40 bg-transparent text-accent hover:border-accent hover:bg-accent-soft disabled:opacity-40",
  ghost:
    "text-fg-muted hover:bg-raised hover:text-fg disabled:text-fg-subtle disabled:hover:bg-transparent",
  danger:
    "border border-danger/40 bg-transparent text-danger hover:bg-danger/10 hover:border-danger disabled:opacity-40",
  /* Deprecated alias, kept because the sign-in page and a handful of wizards
     name it. Same styles as `primary` by definition rather than by coincidence.
     Prefer `primary` in new markup. */
  inverse: PRIMARY,
};

/*
 * A step larger than the customer dashboard's, and left that way deliberately:
 * this console gets worked from a phone during an incident, and `md` at 36px
 * and `lg` at 44px are the sizes that survive a thumb. `sm` at 32px is
 * row-level, where the alternative is a table that fits four rows on a screen.
 *
 * Aligning these *down* to the customer dashboard's 28/32/40 was the obvious
 * consistency win and the wrong one — the two consoles share a vocabulary, not
 * a density, and this is the one that gets read standing up.
 */
/*
 * The second height in each pair is the thumb's. 32 and 36px are comfortable
 * under a cursor that lands where it is pointed and miss under a finger, which
 * is the same reasoning Layout already applies to the drawer button — this
 * extends it to every button rather than the one that was written by hand.
 *
 * `min-h` rather than `h`, so a button that wraps to two lines on a narrow
 * screen grows instead of clipping its own label.
 */
const BUTTON_SIZES = {
  sm: "h-8 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-1.5 px-2.5 text-sm",
  md: "h-9 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-3.5 text-sm",
  lg: "h-11 gap-2 px-5 text-base",
};

/** Shared so anchors and <Link>s can look like buttons without nesting one. */
export function buttonStyles(
  variant: ButtonVariant = "primary",
  size: keyof typeof BUTTON_SIZES = "md",
  className?: string,
): string {
  return cx(
    // `rounded-md` is the control default across both dashboards; `rounded-lg`
    // is for panels and overlays. This was `lg` and read a step softer than the
    // customer console's buttons sitting at the same size.
    "inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors duration-200 ease-smooth disabled:cursor-not-allowed",
    BUTTON_SIZES[size],
    BUTTON_VARIANTS[variant],
    className,
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: keyof typeof BUTTON_SIZES;
  loading?: boolean;
  icon?: IconName;
}

/**
 * `type="button"` unless a caller asks for otherwise.
 *
 * HTML defaults a button inside a form to `submit`, so every ordinary control
 * that happens to sit in one is a submit control. It surfaced in the customer
 * dashboard, where a copy button inside a wizard submitted the form with every
 * field still empty; the same default was here, waiting for the first button
 * that landed inside a form without asking to be one.
 *
 * Safe because every form in this app already marks its own submit control
 * explicitly. Pressing Enter in a text field still submits either way — that
 * path does not go through a button's type.
 */
export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={buttonStyles(variant, size, className)}
    >
      {loading ? (
        <Spinner className="size-4" />
      ) : icon ? (
        <Icon name={icon} className="size-4" />
      ) : null}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx("animate-spin", className ?? "size-5")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}

// ------------------------------------------------------------------ form

export function Field({
  label,
  hint,
  error,
  htmlFor,
  action,
  children,
}: {
  /** A node, so a prompt can set apart what it asks for — see `TypedPhrase`. */
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  htmlFor?: string;
  /**
   * Sits at the far end of the label row. For the one control that has a way
   * out beside it — a password field and its reset link — which belongs level
   * with the label rather than stranded under the input, where it reads as a
   * caption on the field it is not about.
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {/* A row even with no action: one child in a `justify-between` flex sits
          exactly where the block label used to, so nothing else moves. */}
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="block text-sm font-medium text-fg">
          {label}
        </label>
        {action}
      </div>
      {children}
      {error ? (
        <p className="flex items-center gap-1.5 text-sm text-danger">
          <Icon name="warning" className="size-3.5 shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-sm text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Semantic tokens rather than the light/dark pairs this started as: the light
 * half of every pair is dead (see index.css), so each was two chances to get
 * one theme wrong. The rendered values are unchanged — `ink-950` is `canvas`,
 * `ink-700` is `line-strong`, `ink-400` is `fg-subtle` — so this is the same
 * control, described in the vocabulary the rest of the console is moving to.
 */
/*
 * `line` at rest and `line-strong` on hover, which is the customer dashboard's
 * pairing and the direction the semantic layer intends: `line-strong` is
 * documented as a hover state, not an emphasis.
 *
 * This used to sit at `line-strong` at rest with `hover:border-ink-600` — a
 * *lighter* grey from the numbered scale — so the rest state was heavier than
 * the rule allows and the hover lift depended on a colour outside the layer.
 * Swapping the hover to a token without moving the rest state would have made
 * the two identical and silently deleted the hover.
 */
/*
 * `pointer-coarse:text-base` is the one piece of this that is not taste. iOS
 * Safari zooms the viewport in when it focuses a control whose text is under
 * 16px, and does not zoom back out on blur — so one tap on a 14px field leaves
 * a phone at 1.15x with the page wider than the screen and no way back but a
 * pinch. 16px costs the phone nothing: it is the size iOS was going to render
 * anyway, and zooming was how it got there.
 *
 * Keyed on the pointer and not a width breakpoint, because the trigger is the
 * input method rather than the viewport — a desktop window dragged narrow
 * never zooms, and keeps the density this was drawn at.
 */
/*
 * `pointer-coarse:min-h-11` for the thumb, and it does double duty: the
 * show/hide toggle on a password field is positioned `inset-y-0` against the
 * field, so it is exactly as tall as the field is and nothing else can make it
 * a 44px target.
 */
const CONTROL_BASE =
  "w-full rounded-md border border-line text-fg transition-colors duration-200 ease-smooth placeholder:text-fg-subtle hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/25 focus:outline-none disabled:cursor-not-allowed disabled:bg-surface disabled:text-fg-subtle";

/*
 * `md` is the density this console is drawn at: a field in a server form, a
 * plan editor, a model row, where the control is one of thirty things on
 * screen. `xl` is the sign-in surface, where it is one of three.
 *
 * A fixed `h-11` rather than more padding, so the control lands on the 44px
 * touch target by construction instead of by way of `pointer-coarse`.
 */
const CONTROL_SIZES = {
  md: "px-3 py-2 pointer-coarse:min-h-11 text-sm pointer-coarse:text-base",
  xl: "h-11 px-3.5 text-sm pointer-coarse:text-base",
};

/*
 * Which surface the field is sitting on — and the fill is the inverse of it.
 *
 * A field inside a panel is cut *into* it, so it takes the darker canvas. A
 * field on the bare canvas has to stand *out* of it, so it takes surface.
 * Picking the wrong one paints an input exactly the colour of the page behind
 * it, visible only by its hairline.
 *
 * Not folded into the size map: two background utilities in one class list
 * resolve by Tailwind's emission order rather than the order you wrote them,
 * so there is one `bg-*` here and one place that chooses it.
 */
const CONTROL_SURFACES = {
  panel: "bg-canvas",
  canvas: "bg-surface",
};

const CONTROL = cx(CONTROL_BASE, CONTROL_SIZES.md, CONTROL_SURFACES.panel);

/*
 * `size` shadows the HTML attribute of the same name, which is why it is
 * omitted. Nothing here sets `<input size>` — it measures a field in
 * characters, and every field in this console is `w-full`.
 */
export function Input({
  size = "md",
  surface = "panel",
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  size?: keyof typeof CONTROL_SIZES;
  surface?: keyof typeof CONTROL_SURFACES;
}) {
  return (
    <input
      {...rest}
      className={cx(CONTROL_BASE, CONTROL_SIZES[size], CONTROL_SURFACES[surface], className)}
    />
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cx(CONTROL, className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        {...rest}
        className={cx(
          CONTROL,
          "peer cursor-pointer appearance-none pr-9",
          className,
        )}
      >
        {children}
      </select>
      <Icon
        name="chevron"
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-subtle peer-disabled:opacity-50"
      />
    </div>
  );
}

// ------------------------------------------------------------- containers

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("card", className)}>{children}</div>;
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
      {/* `min-w-0` because a flex child will not shrink below its longest
          unbreakable word by default — wrapping moves the action to its own
          line but does nothing for a title that is one long hostname. */}
      <div className="min-w-0">
        <h2 className="font-semibold break-words text-fg">{title}</h2>
        {description && (
          <p className="mt-0.5 text-sm text-fg-subtle">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-fg">
          {title}
        </h1>
        {description && (
          <p className="mt-1.5 text-sm text-fg-subtle">{description}</p>
        )}
      </div>
      {action && <div className="w-full sm:w-auto">{action}</div>}
    </header>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon = "overview",
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      {/* Square, like every other container. Round is reserved for status dots
          and avatars — a circle here read as a leftover from somewhere else. */}
      <span className="grid size-11 place-items-center rounded-md border border-line bg-raised text-fg-subtle">
        <Icon name={icon} className="size-5" />
      </span>
      <h3 className="font-medium text-fg">{title}</h3>
      {description && (
        <p className="max-w-sm text-sm leading-6 text-fg-subtle">{description}</p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

type AlertTone = "error" | "info" | "success" | "warning";

/*
 * Tinted at 8% over the canvas with a hairline at 30%, matching the customer
 * dashboard. These were spelled with raw `red-950/40`, `sky-950/40` and friends
 * — five different numbered scales describing four states the semantic layer
 * already names. `info` in particular reached for `sky`, which is neither this
 * console's accent nor any token in it.
 */
const ALERT_TONES: Record<AlertTone, { styles: string; icon: IconName }> = {
  error: { styles: "border-danger/30 bg-danger/8 text-danger", icon: "warning" },
  info: { styles: "border-accent/30 bg-accent/8 text-accent", icon: "warning" },
  success: { styles: "border-mint/30 bg-mint/8 text-mint", icon: "check" },
  warning: { styles: "border-warn/30 bg-warn/8 text-warn", icon: "warning" },
};

export function Alert({ tone = "error", children }: { tone?: AlertTone; children: ReactNode }) {
  const { styles, icon } = ALERT_TONES[tone];

  return (
    <div
      role="alert"
      className={cx("flex items-start gap-2.5 rounded-md border px-3.5 py-3 text-sm", styles)}
    >
      <Icon name={icon} className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-subtle">
      <Spinner className="size-4" />
      {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("skeleton animate-pulse", className ?? "h-4 w-full")} />;
}

// ------------------------------------------------------------------ table

/**
 * The narrowest viewport a column survives on.
 *
 * A nine-column server table is the right shape on a desk and unusable on a
 * phone, where it used to mean a 640px-wide sideways swipe to reach the column
 * you came for. Rather than reflow rows into cards — which stops being a table,
 * and an operator scanning fifty rows for the one red dot wants a table — each
 * column declares the width it earns its place at, and the rest drop out.
 *
 * Choosing which: keep what identifies the row and what you came to check.
 * Everything you would only read once you had already found the row (created
 * dates, ids, secondary counts) is `md` or `lg`. Nothing that is the *only*
 * copy of a fact on the screen should be hidden — if a column carries something
 * unreachable elsewhere, it stays, and the table scrolls.
 */
export type ColumnPriority = "always" | "sm" | "md" | "lg";

/* Written out rather than interpolated: Tailwind scans source text, so a
   template literal would emit nothing. */
const COLUMN_PRIORITY: Record<ColumnPriority, string> = {
  always: "",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
};

export function Table({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    /*
     * Wide tables scroll inside their own container; the page never does. The
     * hard `min-w-[40rem]` this used to carry is gone — it guaranteed a
     * horizontal scrollbar on every phone regardless of how few columns were
     * actually showing, which defeated the point of dropping any. Column
     * priorities set the width now, and `scroll-shadow-x` says so when the
     * result still overflows.
     */
    <div className="scroll-shadow-x overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead className="border-b border-line text-xs tracking-wide text-fg-subtle uppercase">
          {head}
        </thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  className,
  numeric = false,
  priority = "always",
}: {
  children?: ReactNode;
  className?: string;
  /** Counts and money. Right-aligned so digits line up down the column. */
  numeric?: boolean;
  /** Must match the `Td` beneath it, or the row shears away from its header. */
  priority?: ColumnPriority;
}) {
  return (
    <th
      className={cx(
        "px-4 py-2.5 font-medium",
        numeric && "text-right",
        COLUMN_PRIORITY[priority],
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  title,
  numeric = false,
  priority = "always",
}: {
  children?: ReactNode;
  className?: string;
  /** Native tooltip — used to hang an exact timestamp off a relative one. */
  title?: string;
  /** Pair with the matching `Th` — the base stylesheet already tabularises digits. */
  numeric?: boolean;
  /** Must match the `Th` above it, or the row shears away from its header. */
  priority?: ColumnPriority;
}) {
  return (
    <td
      className={cx("cell", numeric && "text-right", COLUMN_PRIORITY[priority], className)}
      title={title}
    >
      {children}
    </td>
  );
}

/**
 * Loading state for a table, shaped like the table.
 *
 * A centred spinner is a different size from the rows it precedes, so the page
 * jumps when the data lands and anything below it moves. These occupy the space
 * the rows are about to take. `columns` should match the header, and the widths
 * vary per column so the result reads as text rather than a bar chart.
 */
export function TableSkeleton({ columns, rows = 5 }: { columns: number; rows?: number }) {
  const widths = ["w-32", "w-24", "w-20", "w-28", "w-16", "w-24", "w-20"];
  return (
    <div className="divide-y divide-line">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton key={column} className={cx("h-4", widths[column % widths.length])} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- badges

/**
 * A coloured dot carries the status as well as the label does, and survives
 * being skimmed — the eye finds a red dot in a column of fifty servers far
 * faster than it reads the word "unreachable".
 */
function badge(
  styles: string,
  dot: string | null,
  content: ReactNode,
  extra?: string,
  title?: string,
) {
  return (
    <span
      title={title}
      className={cx(
        /* Square, like everything else. The dot inside stays round — it is a
           dot. A column of pills on an otherwise square console is the kind of
           leftover that reads as unfinished rather than as an exception. */
        "inline-flex items-center gap-1.5 border px-2 py-1 text-xs font-medium",
        styles,
        extra,
      )}
    >
      {dot && <span aria-hidden="true" className={cx("size-1.5 rounded-full", dot)} />}
      {content}
    </span>
  );
}

/*
 * Status colour comes off the four semantic tokens and nowhere else: `mint` is
 * up, `warn` is on its way to trouble, `danger` has failed, and neutral is
 * everything deliberately not running.
 *
 * These were raw `emerald-950/60`, `amber-950/60` and `red-950/60` fills with
 * `emerald-500`-class dots. The rendered colours barely move; what changes is
 * that a reserved colour is now actually reserved. `mint` was declared the
 * live-and-healthy signal in both consoles and then never used by the component
 * that meant it — every healthy server rendered a raw emerald instead.
 */
const NEUTRAL = "border-line bg-raised text-fg-muted";
const GOOD = "border-mint/25 bg-mint/8 text-mint";
const WARN = "border-warn/25 bg-warn/8 text-warn";
const BAD = "border-danger/25 bg-danger/8 text-danger";

const DOT_NEUTRAL = "bg-fg-subtle";
const DOT_GOOD = "bg-mint";
const DOT_WARN = "bg-warn";
const DOT_BAD = "bg-danger";

const APP_STATE: Record<AppState, { styles: string; dot: string }> = {
  draft: { styles: NEUTRAL, dot: DOT_NEUTRAL },
  deploying: { styles: WARN, dot: DOT_WARN },
  running: { styles: GOOD, dot: DOT_GOOD },
  stopped: { styles: NEUTRAL, dot: DOT_NEUTRAL },
  error: { styles: BAD, dot: DOT_BAD },
};

/**
 * `stale` means the deployment engine could not be reached, so this is the
 * platform's own record. Dimmed and labelled — an operator diagnosing an
 * incident needs to know which of the two they are looking at.
 */
export function StateBadge({ state, stale = false }: { state: AppState; stale?: boolean }) {
  const { styles, dot } = APP_STATE[state];
  const spinning = state === "deploying" && !stale;
  return badge(
    cx(styles, stale && "opacity-60"),
    spinning ? null : dot,
    <>
      {spinning && <Spinner className="size-3" />}
      {state}
      {stale && <span className="normal-case text-[0.9em] opacity-80">(last known)</span>}
    </>,
    "capitalize",
    stale ? "Could not reach the deployment engine — showing the last recorded state." : undefined,
  );
}

const HEALTH: Record<ServerHealth, { styles: string; dot: string; label: string }> = {
  healthy: { styles: GOOD, dot: DOT_GOOD, label: "Healthy" },
  unknown: { styles: NEUTRAL, dot: DOT_NEUTRAL, label: "Not checked" },
  unreachable: { styles: BAD, dot: DOT_BAD, label: "Unreachable" },
  unauthorized: { styles: BAD, dot: DOT_BAD, label: "Bad credentials" },
};

export function HealthBadge({ health }: { health: ServerHealth }) {
  const { styles, dot, label } = HEALTH[health];
  return badge(styles, dot, label);
}

const SERVER_STATE: Record<ServerState, { styles: string; dot: string }> = {
  active: { styles: GOOD, dot: DOT_GOOD },
  draining: { styles: WARN, dot: DOT_WARN },
  disabled: { styles: NEUTRAL, dot: DOT_NEUTRAL },
};

export function ServerStateBadge({ state }: { state: ServerState }) {
  const { styles, dot } = SERVER_STATE[state];
  return badge(styles, dot, state, "capitalize");
}

export function PlatformRoleBadge({ role }: { role: PlatformRole }) {
  /* The one badge that is allowed to be amber: platform authority is identity,
     not status, which is exactly what the accent means in this console. */
  return badge(
    role === "superadmin" ? "border-accent/30 bg-accent/10 text-accent-hover" : NEUTRAL,
    null,
    role === "superadmin" ? "Superadmin" : "Support",
  );
}

export function RoleBadge({ role }: { role: MemberRole | string }) {
  return badge(NEUTRAL, null, role, "capitalize");
}

export function SuspendedBadge() {
  return badge(BAD, DOT_BAD, "Suspended");
}

type TagTone = "neutral" | "good" | "warn" | "bad" | "accent";

const TAG_TONES: Record<TagTone, string> = {
  neutral: NEUTRAL,
  good: GOOD,
  warn: WARN,
  bad: BAD,
  accent: "border-accent/30 bg-accent/10 text-accent-hover",
};

/**
 * A small label for a value that is a *category* rather than a status — a
 * plan's kind, a payment's outcome, whether a plan is still offered.
 *
 * No dot, which is the whole distinction from the badges above: a dot promises
 * the reader this is live state worth scanning a column for, and spending that
 * signal on "app" versus "database" is what makes it stop working where it
 * matters.
 *
 * These were hand-rolled at four call sites, each with its own `rounded px-1.5`
 * geometry and its own colours. That is how one of them ended up painted
 * `bg-brand-950/60 text-brand-300` — `brand` is the *customer* dashboard's blue
 * scale and is not defined in this console at all, so the "database" tag had
 * been rendering with no fill and inherited text colour for as long as it had
 * existed. One component means there is now one place for a tag to be wrong.
 */
export function Tag({ tone = "neutral", children }: { tone?: TagTone; children: ReactNode }) {
  return badge(TAG_TONES[tone], null, children);
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("ident", className)}>{children}</span>;
}

/**
 * What an operator has to type to confirm, set apart from the sentence around
 * it — "Type [acme-prod] to confirm" — so a name holding spaces, quotes or the
 * prompt's own words still reads as exactly one thing. Mono, because it is
 * typed character for character; breaking anywhere, because an email or a
 * long name has to wrap inside the panel rather than run out of it. The same
 * treatment as the customer dashboard's typed confirmations.
 */
export function TypedPhrase({ children }: { children: ReactNode }) {
  return (
    <span className="rounded border border-line-strong bg-raised px-1.5 py-px font-mono text-[0.92em] break-all text-fg">
      {children}
    </span>
  );
}

// ------------------------------------------------------------------- tabs

/**
 * Underlined tabs for a detail page. Routed rather than stateful, so a tab is a
 * real URL an operator can paste into an incident channel and a colleague lands
 * on the same panel.
 *
 * The strip scrolls horizontally on a narrow viewport rather than wrapping to a
 * second line — a wrapped tab row moves the page content down by a row height
 * the moment one label gets longer, and the underline stops reading as a single
 * rule under a single row.
 *
 * The active tab is underlined in `fg`, not the accent: on a detail page the
 * amber is already carrying the nav rail, and two amber "you are here" marks on
 * one screen is one too many.
 */
export function Tabs({ items }: { items: { to: string; label: string; end?: boolean }[] }) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <nav className="flex min-w-max gap-4 border-b border-line">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cx(
                "-mb-px border-b py-3 text-sm font-medium whitespace-nowrap transition-colors duration-200 ease-smooth",
                isActive ? "border-fg text-fg" : "border-transparent text-fg-subtle hover:text-fg",
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

// --------------------------------------------------------------- dropdown

export interface DropdownProps {
  trigger: ReactNode;
  /** Extra classes for the trigger button. */
  triggerClassName?: string;
  /**
   * Which way the panel opens. `top` is not decoration — a trigger sitting at
   * the bottom of a full-height rail has no room below it, and a panel that
   * opens down there lands off the bottom of the viewport.
   */
  side?: "top" | "bottom";
  /** Horizontal anchoring. `stretch` matches the trigger's own width. */
  align?: "start" | "end" | "stretch";
  /** Overrides for the panel. Geometry belongs in `side`/`align`, not here. */
  menuClassName?: string;
  children: ReactNode;
}

const SIDES = {
  bottom: "top-full mt-1.5",
  top: "bottom-full mb-1.5",
} as const;

const ALIGNS = {
  start: "left-0",
  end: "right-0",
  stretch: "left-0 right-0",
} as const;

const MENU_ITEM_BASE =
  "flex w-full items-center rounded-sm px-2.5 py-1.5 text-left text-[13px] transition-colors duration-200 ease-smooth";

/** One row of a `Dropdown`, written once so no menu's items sit a pixel off another's. */
export const MENU_ITEM = `${MENU_ITEM_BASE} text-fg-muted hover:bg-fg/8 hover:text-fg`;

/**
 * The destructive row. Its own string rather than `cx(MENU_ITEM, "text-danger")`:
 * two text colours on one element are settled by the stylesheet's order, not
 * the class list's, and the muted one comes later -- the red never showed.
 */
export const MENU_ITEM_DANGER = `${MENU_ITEM_BASE} text-danger hover:bg-danger/8`;

/**
 * A popover menu anchored to a trigger button.
 *
 * Click the trigger to toggle, click outside or press Escape to close, and any
 * click inside the panel closes it too — the selection happens on the same
 * click. Arrow keys walk the items, Home and End jump to the ends, and closing
 * hands focus back to the trigger so the tab order does not reset to the top of
 * the page.
 *
 * Children are expected to be links or buttons: that is what the arrow keys move
 * between, and what `role="menu"` here implies.
 *
 * Ported from maxlayer-clients rather than rewritten. Two consoles with two
 * subtly different menu keyboard models is how one of them ends up with the
 * bugs the other already fixed.
 */
export function Dropdown({
  trigger,
  triggerClassName,
  side = "bottom",
  align = "start",
  menuClassName,
  children,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  /* Focus the first item on open. The global `:focus-visible` rule means a mouse
     user never sees the ring, so this costs nothing and makes the menu keyboard
     drivable the moment it appears. */
  useEffect(() => {
    if (open) items()[0]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function items(): HTMLElement[] {
    return Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])") ?? [],
    );
  }

  function move(delta: number) {
    const list = items();
    if (list.length === 0) return;
    const at = list.findIndex((item) => item === document.activeElement);
    const next = at === -1 ? 0 : (at + delta + list.length) % list.length;
    list[next]?.focus();
  }

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        close(true);
        break;
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        event.preventDefault();
        items()[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        items().at(-1)?.focus();
        break;
      case "Tab":
        // Tabbing out of a menu means leaving it, not cycling inside it.
        close(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          // Down-arrow opens, matching every other menu the operator has used.
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cx(
          "inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors duration-200 ease-smooth",
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          onClick={() => setOpen(false)}
          onKeyDown={onMenuKeyDown}
          /*
           * A popover is the one place a shadow earns its keep: `raised` over
           * `surface` is a six-value luminance step, which is not enough on its
           * own to say "this floats above what it covers". The hairline does the
           * rest, at `line-strong` rather than `line` — this panel routinely
           * opens over the rail it lives in, where a `line` edge disappeared.
           */
          className={cx(
            "absolute z-50 min-w-56 rounded-md border border-line-strong bg-raised p-1 shadow-lg",
            SIDES[side],
            ALIGNS[align],
            menuClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
